import { z } from 'zod';
import { optionalInput, phoneSchema } from '../api';
import { withoutBidiControls } from '../text';
import { roleAssignmentSchema, userDepartmentIdsSchema, usernameSchema } from './org';
import { upsertEmployeeSchema } from './hr';
export const PERSON_KEY_PATTERN = /^([ue]):([0-9a-f-]{36})$/i;
export function parsePersonKey(key) {
    const match = PERSON_KEY_PATTERN.exec(key);
    return match ? { kind: match[1].toLowerCase(), id: match[2] } : null;
}
export function personKey(userId, employeeId) {
    if (userId)
        return `u:${userId}`;
    if (employeeId)
        return `e:${employeeId}`;
    throw new Error('A person needs a login or an HR record');
}
export const peopleQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    cursor: z.string().max(600).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: z.enum(['active', 'inactive']).optional(),
});
const blankToUndefined = (value) => (value === '' ? undefined : withoutBidiControls(value));
const blankToNull = (value) => (value === '' ? null : withoutBidiControls(value));
export const personIdentitySchema = z.object({
    name: z.preprocess(withoutBidiControls, z.string().trim().min(2).max(160)),
    nameLocal: z.preprocess(blankToUndefined, z.string().trim().max(160).optional()),
    email: optionalInput(z.string().trim().email()),
    phone: optionalInput(phoneSchema),
    designation: optionalInput(z.string().trim().max(120)),
    branchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    departmentIds: userDepartmentIdsSchema.optional(),
    isActive: z.boolean().default(true),
});
export const personIdentityPatchSchema = z.object({
    name: z.preprocess(withoutBidiControls, z.string().trim().min(2).max(160)).optional(),
    nameLocal: z.preprocess(blankToNull, z.string().trim().max(160).nullable().optional()),
    email: z.string().trim().email().nullable().optional(),
    phone: phoneSchema.nullable().optional(),
    designation: z.string().trim().max(120).nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    departmentIds: userDepartmentIdsSchema.optional(),
    isActive: z.boolean().optional(),
});
export const personLoginSchema = z.object({
    username: usernameSchema,
    password: z.string().min(10).max(200),
    roles: z.array(roleAssignmentSchema).max(50).default([]),
});
export const personClinicalSchema = z.object({
    isDoctor: z.boolean().default(false),
    acceptsAppointments: z.boolean().optional(),
    specialtyId: z.string().uuid().nullable().optional(),
    registrationNo: optionalInput(z.string().trim().max(60)),
    registrationBody: optionalInput(z.string().trim().max(80)),
});
export const personClinicalPatchSchema = z.object({
    isDoctor: z.boolean().optional(),
    acceptsAppointments: z.boolean().optional(),
    specialtyId: z.string().uuid().nullable().optional(),
    registrationNo: z.string().trim().max(60).nullable().optional(),
    registrationBody: z.string().trim().max(80).nullable().optional(),
});
export const personEmploymentSchema = upsertEmployeeSchema.omit({ name: true, userId: true });
const clearableText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable().optional());
export const personEmploymentPatchSchema = personEmploymentSchema.partial().extend({
    cnic: clearableText(20),
    phone: clearableText(20),
    email: z.preprocess(blankToNull, z.string().trim().email().max(160).nullable().optional()),
    designation: clearableText(120),
    licenseNo: clearableText(60),
    bankAccount: clearableText(60),
});
const doctorHasRegistration = (clinical) => !clinical?.isDoctor || Boolean(clinical.registrationNo?.trim());
export const createPersonSchema = z
    .object({
    identity: personIdentitySchema,
    login: personLoginSchema.optional(),
    clinical: personClinicalSchema.optional(),
    employment: personEmploymentSchema.optional(),
})
    .refine((value) => Boolean(value.login || value.employment), {
    path: ['login'],
    message: 'Give this person a login, an HR record, or both',
})
    .refine((value) => !value.clinical || Boolean(value.login), {
    path: ['clinical'],
    message: 'Clinical details belong to a login',
})
    .refine((value) => doctorHasRegistration(value.clinical), {
    path: ['clinical', 'registrationNo'],
    message: 'A doctor needs a registration number',
});
export const updatePersonSchema = z
    .object({
    identity: personIdentityPatchSchema.optional(),
    login: personLoginSchema.optional(),
    clinical: personClinicalPatchSchema.optional(),
    employment: personEmploymentPatchSchema.optional(),
})
    .refine((value) => Boolean(value.identity || value.login || value.clinical || value.employment), {
    path: ['identity'],
    message: 'Nothing to change',
})
    .refine((value) => !value.login || doctorHasRegistration(value.clinical), {
    path: ['clinical', 'registrationNo'],
    message: 'A doctor needs a registration number',
});
//# sourceMappingURL=people.js.map